/**
 * Printing Utility for Restaurant OS
 * Supports browser printing with CSS and ESC/POS formatting
 * Includes VeriFactu compliance for Spain
 */

// ==================== RECEIPT TEMPLATES ====================

/**
 * Generate HTML receipt for browser printing
 * @param {Object} order - Order data
 * @param {Object} restaurant - Restaurant data
 * @param {Object} options - Print options
 * @param {Object} verifactu - VeriFactu fiscal invoice data (optional)
 */
export const generateReceiptHTML = (order, restaurant, options = {}, verifactu = null) => {
  const {
    showLogo = true,
    showTip = true,
    showPaymentMethod = true,
    copies = 1
  } = options;

  const formatCurrency = (amount, currency = '€') => `${currency}${(amount || 0).toFixed(2)}`;
  const formatDate = (date) => new Date(date).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const items = order.items || [];
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tip = order.tip_amount || 0;
  const total = subtotal + tip;

  // VeriFactu section HTML
  const verifactuSection = verifactu ? generateVerifactuSection(verifactu) : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Receipt - ${order.id || 'Order'}</title>
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          width: 80mm;
          max-width: 80mm;
          padding: 5mm;
          background: white;
          color: black;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        .restaurant-name {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .restaurant-info {
          font-size: 10px;
          color: #333;
        }
        .fiscal-info {
          font-size: 9px;
          color: #555;
          margin-top: 5px;
        }
        .order-info {
          margin-bottom: 10px;
          font-size: 11px;
        }
        .order-info .label {
          display: inline-block;
          width: 80px;
        }
        .items {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 10px 0;
          margin: 10px 0;
        }
        .item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
        }
        .item-name {
          flex: 1;
        }
        .item-qty {
          width: 30px;
          text-align: right;
          margin-right: 10px;
        }
        .item-price {
          width: 60px;
          text-align: right;
        }
        .item-notes {
          font-size: 10px;
          color: #666;
          margin-left: 30px;
          margin-bottom: 5px;
        }
        .totals {
          margin: 10px 0;
        }
        .total-line {
          display: flex;
          justify-content: space-between;
          margin-bottom: 3px;
        }
        .total-line.grand {
          font-size: 16px;
          font-weight: bold;
          border-top: 2px solid #000;
          padding-top: 5px;
          margin-top: 5px;
        }
        .payment-info {
          margin: 10px 0;
          padding: 10px 0;
          border-top: 1px dashed #000;
          font-size: 11px;
        }
        .verifactu-section {
          margin: 15px 0;
          padding: 10px;
          border: 2px solid #000;
          background: #f9f9f9;
        }
        .verifactu-header {
          text-align: center;
          font-weight: bold;
          font-size: 11px;
          margin-bottom: 8px;
          padding-bottom: 5px;
          border-bottom: 1px solid #000;
        }
        .verifactu-legend {
          text-align: center;
          font-size: 9px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .verifactu-data {
          font-size: 9px;
          line-height: 1.3;
        }
        .verifactu-data div {
          margin-bottom: 2px;
        }
        .verifactu-qr {
          text-align: center;
          margin: 10px 0;
        }
        .verifactu-qr img {
          width: 80px;
          height: 80px;
        }
        .verifactu-hash {
          font-family: monospace;
          font-size: 7px;
          word-break: break-all;
          text-align: center;
          color: #666;
        }
        .iva-breakdown {
          font-size: 9px;
          margin-top: 5px;
          padding-top: 5px;
          border-top: 1px dotted #000;
        }
        .iva-line {
          display: flex;
          justify-content: space-between;
        }
        .footer {
          text-align: center;
          margin-top: 15px;
          padding-top: 10px;
          border-top: 1px dashed #000;
          font-size: 10px;
        }
        .footer p {
          margin-bottom: 3px;
        }
        .barcode {
          text-align: center;
          margin: 10px 0;
          font-family: 'Libre Barcode 39', cursive;
          font-size: 30px;
        }
        @media print {
          body {
            width: 80mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${showLogo ? `<div class="restaurant-name">${restaurant?.name || 'Restaurant'}</div>` : ''}
        <div class="restaurant-info">
          ${restaurant?.address || ''}<br>
          ${restaurant?.phone ? `Tel: ${restaurant.phone}` : ''}
        </div>
        ${verifactu ? `
          <div class="fiscal-info">
            NIF: ${verifactu.nif_emisor || ''}<br>
            ${verifactu.direccion_fiscal || ''}
          </div>
        ` : ''}
      </div>

      <div class="order-info">
        <div><span class="label">Order #:</span> ${order.id?.slice(-8).toUpperCase() || 'N/A'}</div>
        <div><span class="label">Date:</span> ${formatDate(order.created_at || new Date())}</div>
        ${verifactu ? `<div><span class="label">Factura:</span> ${verifactu.invoice_series}-${String(verifactu.invoice_number).padStart(5, '0')}</div>` : ''}
        ${order.table_number ? `<div><span class="label">Table:</span> ${order.table_number}</div>` : ''}
        ${order.customer_name ? `<div><span class="label">Customer:</span> ${order.customer_name}</div>` : ''}
        ${order.order_type ? `<div><span class="label">Type:</span> ${order.order_type.replace('_', ' ').toUpperCase()}</div>` : ''}
      </div>

      <div class="items">
        ${items.map(item => `
          <div class="item">
            <span class="item-qty">${item.quantity}x</span>
            <span class="item-name">${item.name}</span>
            <span class="item-price">${formatCurrency(item.price * item.quantity)}</span>
          </div>
          ${item.notes ? `<div class="item-notes">→ ${item.notes}</div>` : ''}
        `).join('')}
      </div>

      <div class="totals">
        <div class="total-line">
          <span>Subtotal</span>
          <span>${formatCurrency(subtotal)}</span>
        </div>
        ${showTip && tip > 0 ? `
          <div class="total-line">
            <span>Tip</span>
            <span>${formatCurrency(tip)}</span>
          </div>
        ` : ''}
        <div class="total-line grand">
          <span>TOTAL</span>
          <span>${formatCurrency(total)}</span>
        </div>
      </div>

      ${showPaymentMethod && order.payment_method ? `
        <div class="payment-info">
          <div><strong>Payment:</strong> ${order.payment_method.toUpperCase()}</div>
          ${order.payment_method === 'cash' && order.cash_received ? `
            <div>Cash Received: ${formatCurrency(order.cash_received)}</div>
            <div>Change: ${formatCurrency(order.cash_received - total)}</div>
          ` : ''}
        </div>
      ` : ''}

      ${verifactuSection}

      <div class="footer">
        <p>Thank you for dining with us!</p>
        <p>Please come again</p>
        ${restaurant?.website ? `<p>${restaurant.website}</p>` : ''}
      </div>

      <div class="barcode">*${(order.id || '000000').slice(-8).toUpperCase()}*</div>
    </body>
    </html>
  `;
};

/**
 * Generate VeriFactu section for receipts
 * Includes QR code, fiscal data, and AEAT verification info
 */
const generateVerifactuSection = (verifactu) => {
  if (!verifactu) return '';

  const qrDataUrl = generateQRCodeDataURL(verifactu.qr_verification_url);
  
  return `
    <div class="verifactu-section">
      <div class="verifactu-legend">
        VERI*FACTU
      </div>
      <div class="verifactu-header">
        Factura verificable en sede.agenciatributaria.gob.es
      </div>
      
      <div class="verifactu-data">
        <div><strong>Factura:</strong> ${verifactu.invoice_series}-${String(verifactu.invoice_number).padStart(5, '0')}</div>
        <div><strong>Fecha:</strong> ${verifactu.invoice_date}</div>
        <div><strong>NIF Emisor:</strong> ${verifactu.nif_emisor}</div>
        <div><strong>Tipo:</strong> ${verifactu.tipo_factura}</div>
      </div>
      
      ${verifactu.iva_breakdown && verifactu.iva_breakdown.length > 0 ? `
        <div class="iva-breakdown">
          <strong>Desglose IVA:</strong>
          ${verifactu.iva_breakdown.map(iva => `
            <div class="iva-line">
              <span>Base ${iva.rate}%:</span>
              <span>€${iva.base.toFixed(2)} + €${iva.cuota.toFixed(2)}</span>
            </div>
          `).join('')}
        </div>
      ` : ''}
      
      <div class="verifactu-qr">
        <img src="${qrDataUrl}" alt="QR Verificación AEAT" />
      </div>
      
      <div class="verifactu-hash">
        Hash: ${verifactu.huella_hash?.substring(0, 16)}...
      </div>
    </div>
  `;
};

/**
 * Generate QR code as data URL
 * Uses a simple text-based QR placeholder for now
 * TODO: Integrate with qrcode.react for actual QR generation
 */
const generateQRCodeDataURL = (url) => {
  // For server-side/print preview, we use a placeholder
  // The actual QR is generated by the QRCode component in React
  // This returns a placeholder SVG for the print template
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
      <rect width="80" height="80" fill="white"/>
      <rect x="5" y="5" width="20" height="20" fill="black"/>
      <rect x="55" y="5" width="20" height="20" fill="black"/>
      <rect x="5" y="55" width="20" height="20" fill="black"/>
      <rect x="10" y="10" width="10" height="10" fill="white"/>
      <rect x="60" y="10" width="10" height="10" fill="white"/>
      <rect x="10" y="60" width="10" height="10" fill="white"/>
      <rect x="12" y="12" width="6" height="6" fill="black"/>
      <rect x="62" y="12" width="6" height="6" fill="black"/>
      <rect x="12" y="62" width="6" height="6" fill="black"/>
      <text x="40" y="45" text-anchor="middle" font-size="6" fill="black">SCAN</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Generate kitchen ticket HTML
 */
export const generateKitchenTicketHTML = (order, options = {}) => {
  const {
    station = null, // Filter items by station
    showAllergies = true,
    showMods = true,
    priority = 'normal' // 'rush', 'normal'
  } = options;

  const formatTime = (date) => new Date(date).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit'
  });

  let items = order.items || [];
  if (station) {
    items = items.filter(item => item.station === station);
  }

  const allergies = order.allergies || order.allergy_info || [];
  const priorityClass = priority === 'rush' ? 'rush' : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Kitchen Ticket - ${order.id || 'Order'}</title>
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.3;
          width: 80mm;
          max-width: 80mm;
          padding: 5mm;
          background: white;
          color: black;
        }
        .ticket {
          border: 2px solid #000;
          padding: 10px;
        }
        .ticket.rush {
          border: 4px solid #000;
        }
        .ticket.rush .header {
          background: #000;
          color: #fff;
          margin: -10px -10px 10px -10px;
          padding: 10px;
        }
        .header {
          text-align: center;
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        .order-number {
          font-size: 24px;
          font-weight: bold;
        }
        .rush-label {
          background: #000;
          color: #fff;
          padding: 2px 10px;
          display: inline-block;
          margin-bottom: 5px;
          font-weight: bold;
        }
        .time-info {
          font-size: 16px;
          margin-top: 5px;
        }
        .table-info {
          font-size: 20px;
          font-weight: bold;
          margin: 10px 0;
          padding: 5px;
          background: #eee;
          text-align: center;
        }
        .station-label {
          font-size: 12px;
          text-transform: uppercase;
          background: #000;
          color: #fff;
          padding: 3px 8px;
          display: inline-block;
          margin-bottom: 10px;
        }
        .items {
          margin: 10px 0;
        }
        .item {
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 1px dashed #ccc;
        }
        .item:last-child {
          border-bottom: none;
        }
        .item-header {
          display: flex;
          font-size: 16px;
          font-weight: bold;
        }
        .item-qty {
          min-width: 35px;
          font-size: 20px;
        }
        .item-name {
          flex: 1;
        }
        .item-mods {
          margin-left: 35px;
          font-size: 12px;
          color: #333;
        }
        .item-notes {
          margin-left: 35px;
          font-size: 12px;
          font-style: italic;
          background: #ffe;
          padding: 3px 5px;
          margin-top: 3px;
        }
        .allergies {
          background: #fcc;
          border: 2px solid #c00;
          padding: 10px;
          margin: 10px 0;
          text-align: center;
        }
        .allergies-title {
          font-weight: bold;
          font-size: 14px;
          color: #c00;
          margin-bottom: 5px;
        }
        .allergies-list {
          font-size: 16px;
          font-weight: bold;
        }
        .footer {
          text-align: center;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 2px solid #000;
          font-size: 12px;
        }
        @media print {
          body {
            width: 80mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="ticket ${priorityClass}">
        <div class="header">
          ${priority === 'rush' ? '<div class="rush-label">🔥 RUSH ORDER 🔥</div>' : ''}
          <div class="order-number">#${(order.id || '000000').slice(-6).toUpperCase()}</div>
          <div class="time-info">${formatTime(order.created_at || new Date())}</div>
        </div>

        ${order.table_number ? `
          <div class="table-info">TABLE ${order.table_number}</div>
        ` : order.order_type === 'takeaway' ? `
          <div class="table-info">TAKEAWAY</div>
        ` : order.order_type === 'delivery' ? `
          <div class="table-info">DELIVERY</div>
        ` : ''}

        ${station ? `<div class="station-label">${station.toUpperCase()}</div>` : ''}

        ${showAllergies && allergies.length > 0 ? `
          <div class="allergies">
            <div class="allergies-title">⚠️ ALLERGY ALERT ⚠️</div>
            <div class="allergies-list">${allergies.join(', ')}</div>
          </div>
        ` : ''}

        <div class="items">
          ${items.map(item => `
            <div class="item">
              <div class="item-header">
                <span class="item-qty">${item.quantity}x</span>
                <span class="item-name">${item.name}</span>
              </div>
              ${showMods && item.modifiers?.length ? `
                <div class="item-mods">
                  ${item.modifiers.map(m => `+ ${m}`).join('<br>')}
                </div>
              ` : ''}
              ${item.notes ? `
                <div class="item-notes">NOTE: ${item.notes}</div>
              ` : ''}
            </div>
          `).join('')}
        </div>

        ${order.notes ? `
          <div class="item-notes" style="margin-left: 0;">
            <strong>Order Notes:</strong> ${order.notes}
          </div>
        ` : ''}

        <div class="footer">
          ${order.customer_name ? `Customer: ${order.customer_name}` : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

// ==================== PRINT FUNCTIONS ====================

/**
 * Open print window with HTML content
 */
export const printHTML = (html, options = {}) => {
  const { title = 'Print', onAfterPrint = null } = options;

  const printWindow = window.open('', '_blank', 'width=400,height=600');
  if (!printWindow) {
    console.error('Failed to open print window - popup blocked?');
    return false;
  }

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.document.title = title;

  // Wait for content to load, then print
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    
    // Close window after print dialog
    printWindow.onafterprint = () => {
      printWindow.close();
      if (onAfterPrint) onAfterPrint();
    };
  };

  return true;
};

/**
 * Print customer receipt
 * @param {Object} order - Order data
 * @param {Object} restaurant - Restaurant data
 * @param {Object} options - Print options
 * @param {Object} verifactu - VeriFactu fiscal invoice data (optional)
 */
export const printReceipt = (order, restaurant, options = {}, verifactu = null) => {
  const html = generateReceiptHTML(order, restaurant, options, verifactu);
  return printHTML(html, { 
    title: `Receipt - ${order.id?.slice(-8) || 'Order'}`,
    ...options 
  });
};

/**
 * Print VeriFactu compliant receipt (fetches fiscal invoice automatically)
 */
export const printVerifactuReceipt = async (order, restaurant, api, options = {}) => {
  let verifactu = null;
  
  try {
    // Try to fetch fiscal invoice for this order
    const response = await api.get(`/verifactu/invoices/${restaurant.id}?order_id=${order.id}`);
    if (response.data?.invoices?.length > 0) {
      verifactu = response.data.invoices[0];
    }
  } catch (error) {
    console.warn('Could not fetch VeriFactu invoice:', error);
  }
  
  return printReceipt(order, restaurant, options, verifactu);
};

/**
 * Print kitchen ticket
 */
export const printKitchenTicket = (order, options = {}) => {
  const html = generateKitchenTicketHTML(order, options);
  return printHTML(html, { 
    title: `Kitchen - ${order.id?.slice(-8) || 'Order'}`,
    ...options 
  });
};

/**
 * Print multiple kitchen tickets (one per station)
 */
export const printKitchenTicketsByStation = (order, stations = [], options = {}) => {
  const items = order.items || [];
  const orderStations = [...new Set(items.map(i => i.station).filter(Boolean))];
  const targetStations = stations.length > 0 ? stations : orderStations;

  targetStations.forEach((station, index) => {
    const stationItems = items.filter(i => i.station === station);
    if (stationItems.length > 0) {
      // Delay each print slightly to avoid overwhelming the browser
      setTimeout(() => {
        printKitchenTicket(order, { ...options, station });
      }, index * 500);
    }
  });
};

// ==================== ESC/POS COMMANDS ====================
// These are for future Print Bridge integration

export const ESC_POS = {
  // Initialization
  INIT: '\x1B\x40',
  
  // Text formatting
  BOLD_ON: '\x1B\x45\x01',
  BOLD_OFF: '\x1B\x45\x00',
  UNDERLINE_ON: '\x1B\x2D\x01',
  UNDERLINE_OFF: '\x1B\x2D\x00',
  DOUBLE_HEIGHT: '\x1B\x21\x10',
  DOUBLE_WIDTH: '\x1B\x21\x20',
  DOUBLE_SIZE: '\x1B\x21\x30',
  NORMAL_SIZE: '\x1B\x21\x00',
  
  // Alignment
  ALIGN_LEFT: '\x1B\x61\x00',
  ALIGN_CENTER: '\x1B\x61\x01',
  ALIGN_RIGHT: '\x1B\x61\x02',
  
  // Paper
  CUT_PAPER: '\x1D\x56\x00',
  CUT_PAPER_PARTIAL: '\x1D\x56\x01',
  FEED_LINE: '\x0A',
  FEED_3_LINES: '\x0A\x0A\x0A',
  
  // Cash drawer
  CASH_DRAWER_KICK: '\x1B\x70\x00\x19\xFA', // Pin 2
  CASH_DRAWER_KICK_PIN5: '\x1B\x70\x01\x19\xFA', // Pin 5
  
  // Barcode
  BARCODE_HEIGHT: '\x1D\x68\x50', // 80 dots height
  BARCODE_WIDTH: '\x1D\x77\x02', // Medium width
  BARCODE_PRINT: '\x1D\x6B\x02', // Code 39
};

/**
 * Generate ESC/POS commands for receipt
 * (For use with Print Bridge when implemented)
 */
export const generateESCPOSReceipt = (order, restaurant, options = {}) => {
  const { openCashDrawer = false, cutPaper = true } = options;
  const formatCurrency = (amount) => `£${(amount || 0).toFixed(2)}`;
  
  let commands = '';
  
  // Initialize printer
  commands += ESC_POS.INIT;
  
  // Open cash drawer if requested
  if (openCashDrawer) {
    commands += ESC_POS.CASH_DRAWER_KICK;
  }
  
  // Restaurant name (centered, large)
  commands += ESC_POS.ALIGN_CENTER;
  commands += ESC_POS.DOUBLE_SIZE;
  commands += (restaurant?.name || 'Restaurant') + '\n';
  commands += ESC_POS.NORMAL_SIZE;
  
  // Address
  if (restaurant?.address) {
    commands += restaurant.address + '\n';
  }
  if (restaurant?.phone) {
    commands += `Tel: ${restaurant.phone}\n`;
  }
  
  commands += '\n' + '-'.repeat(32) + '\n';
  
  // Order info
  commands += ESC_POS.ALIGN_LEFT;
  commands += `Order: ${(order.id || '').slice(-8).toUpperCase()}\n`;
  commands += `Date: ${new Date(order.created_at || new Date()).toLocaleString()}\n`;
  if (order.table_number) {
    commands += `Table: ${order.table_number}\n`;
  }
  
  commands += '-'.repeat(32) + '\n\n';
  
  // Items
  const items = order.items || [];
  items.forEach(item => {
    const price = formatCurrency(item.price * item.quantity);
    const name = item.name.substring(0, 24);
    commands += `${item.quantity}x ${name.padEnd(22)} ${price.padStart(6)}\n`;
    if (item.notes) {
      commands += `   -> ${item.notes}\n`;
    }
  });
  
  commands += '\n' + '-'.repeat(32) + '\n';
  
  // Totals
  const subtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
  const tip = order.tip_amount || 0;
  const total = subtotal + tip;
  
  commands += `${'Subtotal'.padEnd(26)}${formatCurrency(subtotal).padStart(6)}\n`;
  if (tip > 0) {
    commands += `${'Tip'.padEnd(26)}${formatCurrency(tip).padStart(6)}\n`;
  }
  commands += ESC_POS.BOLD_ON;
  commands += `${'TOTAL'.padEnd(26)}${formatCurrency(total).padStart(6)}\n`;
  commands += ESC_POS.BOLD_OFF;
  
  // Footer
  commands += '\n' + '-'.repeat(32) + '\n';
  commands += ESC_POS.ALIGN_CENTER;
  commands += 'Thank you for dining with us!\n';
  commands += 'Please come again\n';
  
  // Cut paper
  commands += ESC_POS.FEED_3_LINES;
  if (cutPaper) {
    commands += ESC_POS.CUT_PAPER_PARTIAL;
  }
  
  return commands;
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Check if printing is supported
 */
export const isPrintingSupported = () => {
  return typeof window !== 'undefined' && typeof window.print === 'function';
};

/**
 * Get available print options
 */
export const getPrintOptions = () => {
  return {
    browserPrint: isPrintingSupported(),
    printBridge: false, // Will be true when Print Bridge is connected
    escpos: false // Will be true when ESC/POS printer is configured
  };
};

export default {
  generateReceiptHTML,
  generateKitchenTicketHTML,
  printHTML,
  printReceipt,
  printKitchenTicket,
  printKitchenTicketsByStation,
  generateESCPOSReceipt,
  isPrintingSupported,
  getPrintOptions,
  ESC_POS
};
